// src/views/BOM/components/ValidationPanel.js
import React, { useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Grid,
    IconButton,
    Paper,
    Typography,
    Divider,
    Checkbox,
    FormControlLabel,
    RadioGroup,
    Radio,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Avatar
} from '@mui/material';
import {
    CloudUpload as CloudUploadIcon,
    DeleteForever as DeleteForeverIcon,
    AttachFile as AttachFileIcon,
    GetApp as DownloadIcon,
    Person as PersonIcon,
    Circle as CircleIcon
} from '@mui/icons-material';
import moment from 'moment';

const ValidationPanel = ({ data = {}, onUpdate, commercialDetails = {}, onCommercialUpdate, isReadOnly, currentUser }) => {

    const [dragActive, setDragActive] = useState({});
    const [newLogComment, setNewLogComment] = useState("");
    const [newLogFiles, setNewLogFiles] = useState([]);

    const isPanelReadOnly = isReadOnly || !!data.validation_completed;



    // --- Data Handlers ---

    const handleChange = (field, value) => {
        onUpdate({ ...data, [field]: value });
    };

    const handleFileAdd = (field, newFiles) => {
        const currentFiles = data[field] || [];
        const mappedFiles = Array.from(newFiles).map(f => ({
            file_name: f.name,
            file_url: URL.createObjectURL(f),
            file_type: f.type,
            originFileObj: f,
            uploaded_at: new Date()
        }));
        handleChange(field, [...currentFiles, ...mappedFiles]);
    };

    const handleFileRemove = (field, index) => {
        const currentFiles = data[field] || [];
        const updatedFiles = currentFiles.filter((_, i) => i !== index);
        handleChange(field, updatedFiles);
    };

    const handleAddLog = () => {
      if (!newLogComment.trim() && newLogFiles.length === 0) return;

      const userName = currentUser?.first_name 
        ? `${currentUser.first_name} ${currentUser.last_name || ''}`.trim() 
        : currentUser?.name || 'User';

      const newEntry = {
          comment: newLogComment,
          attachments: newLogFiles,
          added_by: userName,
          timestamp: new Date()
      };

      // FIXED: Key name changed from 'order_requirements_log' to 'order_requirements' to match Schema
      const updatedLogs = [...(data.order_requirements || []), newEntry];
      handleChange('order_requirements', updatedLogs);
      
      setNewLogComment("");
      setNewLogFiles([]);
  };
    
    // --- Render Helpers ---

    const renderHeader = (title) => (
        <div className="col-12 mt-4 mb-3">
            <Box sx={{ 
                bgcolor: '#f4f6f8', 
                borderLeft: '4px solid #2d3d42', 
                p: 1, 
                px: 2,
                borderRadius: '4px',
                mb: 1,
            }}>
                <Typography variant="subtitle1" sx={{ color: '#2d3d42', fontWeight: 700, fontSize: '15px !important', mb: 0 }}>
                    {title}
                </Typography>
            </Box>
        </div>
    );

    const renderSubHeader = (title) => (
        <div className="col-12 mt-3 mb-2">
            <Typography variant="subtitle2" sx={{ color: '#2d3d42', fontWeight: 600, mb: 1, fontSize: '14px !important' }}>
                {title}
            </Typography>
            <Divider sx={{ mb: 2 }} />
        </div>
    );

    const renderLabel = (label, required = false, currentReadOnly = isPanelReadOnly) => (
        <label className={currentReadOnly ? 'readonly-label form-label d-block bom-label' : 'form-label d-block bom-label'}>
            {label} {required && <span style={{ color: 'red' }}>*</span>}
        </label>
    );

    const renderInput = (label, fieldKey, type = "text", placeholder = "", required = false, width = 12, forceReadOnly = null) => {
        const currentReadOnly = forceReadOnly !== null ? forceReadOnly : isPanelReadOnly;
        return (
            <div className={`col-md-${width} mb-3`}>
                {renderLabel(label, required, currentReadOnly)}
                <input
                    type={type}
                    className={`form-control-customer bom-input ${currentReadOnly ? 'readonly-input' : ''}`}
                    value={data[fieldKey] || ''}
                    placeholder={placeholder}
                    disabled={currentReadOnly}
                    onChange={(e) => handleChange(fieldKey, e.target.value)}
                />
            </div>
        );
    };

    const renderDropZone = (label, fieldKey, filesData, width = 12, extraClass = "mb-3", forceReadOnly = null) => {
        const files = Array.isArray(filesData) ? filesData : [];
        const isDrag = dragActive[fieldKey];
        const currentReadOnly = forceReadOnly !== null ? forceReadOnly : isPanelReadOnly;

        const containerClass = width ? `col-md-${width} ${extraClass}` : extraClass;

        return (
            <div className={containerClass.trim()}>
                {label && renderLabel(label, false, currentReadOnly)}
                
                {/* File List Chips */}
                {files.length > 0 && (
                    <Box className="bom-chip-container" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                        {files.map((f, i) => (
                            <Chip
                                key={i}
                                size="small"
                                icon={<AttachFileIcon />}
                                label={f.file_name || f.originalname || "attachment"}
                                onClick={() => (f.file_url || f.s3_url) && window.open(f.file_url || f.s3_url, '_blank')}
                                onDelete={!currentReadOnly ? () => handleFileRemove(fieldKey, i) : undefined}
                                deleteIcon={<DeleteForeverIcon />}
                                variant="outlined"
                                title={f.file_name || f.originalname || "attachment"}
                                clickable={!!(f.file_url || f.s3_url)}
                                sx={{
                                    maxWidth: 200,
                                    '& .MuiChip-label': {
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    },
                                }}
                            />
                        ))}
                    </Box>
                )}

                {/* Drop Area */}
                {!currentReadOnly && (
                    <Box
                        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive({ ...dragActive, [fieldKey]: true }); }}
                        onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive({ ...dragActive, [fieldKey]: true }); }}
                        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragActive({ ...dragActive, [fieldKey]: false }); }}
                        onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDragActive({ ...dragActive, [fieldKey]: false });
                            if (e.dataTransfer.files?.length) handleFileAdd(fieldKey, e.dataTransfer.files);
                        }}
                        sx={{
                            border: '2px dashed',
                            borderColor: isDrag ? '#2d3d42' : '#ccc',
                            borderRadius: 1,
                            p: 2,
                            textAlign: 'center',
                            backgroundColor: isDrag ? '#f0f8ff' : '#fafafa',
                            transition: 'all 0.2s',
                            cursor: 'default',
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            gap: 1
                        }}
                    >
                        <Typography variant="body2" color="textSecondary" sx={{fontSize: '12px'}}>
                            Drag & drop files here, or
                        </Typography>
                        <Button
                            variant="outlined"
                            component="label"
                            size="small"
                            startIcon={<CloudUploadIcon />}
                            sx={{ textTransform: 'none', borderColor: '#ccc', color: '#555', bgcolor: 'white' }}
                        >
                            Browse Files
                            <input type="file" hidden multiple onChange={(e) => e.target.files?.length && handleFileAdd(fieldKey, e.target.files)} />
                        </Button>
                    </Box>
                )}
            </div>
        );
    };

    // Specific Yes/No Render for Validation Checklist
    const renderValidationCheck = (label, fieldKey, commentKey, attachKey) => (
        <div className="col-md-6 mb-3">
            <Paper elevation={0} sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: '4px', height: '100%', bgcolor: '#ffffff' }}>
                <Box mb={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <label className={isPanelReadOnly ? 'readonly-label form-label bom-label mb-0' : 'form-label bom-label mb-0'} style={{ flex: 1 }}>
                        {label}
                    </label>
                    <RadioGroup
                        row
                        value={data[fieldKey] || ''}
                        onChange={(e) => handleChange(fieldKey, e.target.value)}
                    >
                        <FormControlLabel value="Yes" disabled={isPanelReadOnly} control={<Radio sx={{ color: '#2d3d42', '&.Mui-checked': { color: '#2d3d42' } }} />} label={<span className="bom-label" style={{ fontWeight: 700 }}>YES</span>} />
                        <FormControlLabel value="No" disabled={isPanelReadOnly} control={<Radio sx={{ color: '#d32f2f', '&.Mui-checked': { color: '#d32f2f' } }} />} label={<span className="bom-label" style={{ fontWeight: 700 }}>NO</span>} />
                    </RadioGroup>
                </Box>
                
                {/* Conditional Comment Box for "No" or Issues */}
                {data[fieldKey] === 'No' && (
                    <Box mt={1} p={1} bgcolor="#fff1f0" borderRadius="4px" display="flex" flexDirection="column" gap={1}>
                        <input
                            className="form-control-customer bom-input"
                            placeholder="Describe the issue..."
                            value={data[commentKey] || ''}
                            onChange={(e) => handleChange(commentKey, e.target.value)}
                            style={{ backgroundColor: '#fff', width: '100%', boxSizing: 'border-box' }}
                            disabled={isPanelReadOnly}
                        />
                        {renderDropZone(null, attachKey, data[attachKey], null, "w-100", isPanelReadOnly)}
                    </Box>
                )}
            </Paper>
        </div>
    );

    return (
        <div className="row">
      
          {/* ===================== 1. FIT AND FUNCTION ===================== */}
          {renderHeader("1. Fit and Function")}
      
            {/* a. Complete scope received */}
            {renderValidationCheck(
              "Complete scope received as per order?",
              "scope_received",
              "scope_received_comment",
              "scope_received_attachments"
            )}
      
            {/* b. Items condition */}
            {renderValidationCheck(
              "All fixture items received in good condition?",
              "items_condition_ok",
              "items_condition_comment",
              "items_condition_attachments"
            )}
      
            {/* ---------- c. Pre-requisites (Machine Side) ---------- */}
            {renderSubHeader("Pre-requisites – Machine Side")}
        
            {renderInput("Table Face Runout (microns)", "table_face_runout", "number", "0.00", false, 3)}
            {renderInput("Table Radial Runout (microns)", "table_radial_runout", "number", "0.00", false, 3)}
            {renderInput("Tailstock Runout (microns)", "tailstock_runout", "number", "0.00", false, 3)}
            {renderInput("Tailstock Alignment (microns)", "tailstock_alignment", "number", "0.00", false, 3)}
      
            {/* d. Fixture mounted */}
            {renderValidationCheck(
              "Fixture is mounted correctly?",
              "fixture_mounted_correctly",
              "fixture_mounted_comment",
              "fixture_mounted_attachments"
            )}
      
            {/* e. Clamp / Declamp */}
            {renderValidationCheck(
              "Clamp / Declamp is OK?",
              "clamp_declamp_ok",
              "clamp_declamp_comment",
              "clamp_declamp_attachments"
            )}
      
            {/* ---------- f. Runout of Master / Control Arbor ---------- */}
            {renderSubHeader("Runout of Master / Control Arbor / Control Ring")}
        
            {renderInput("Radial Runout (microns)", "control_radial_runout", "number", "0.00", false, 6)}
            {renderInput("Axial Runout (microns)", "control_axial_runout", "number", "0.00", false, 6)}
      
            {/* g. Fit & Function OK */}
            {renderValidationCheck(
              "Fit and Function OK?",
              "fit_function_ok",
              "fit_function_comment",
              "fit_function_fail_attachments"
            )}
      
            {/* h. Remarks */}
            {renderSubHeader("Remarks")}
        
            {renderInput(
              "General Remarks / Observations",
              "remarks",
              "text",
              "Enter remarks",
              false,
              12
            )}
      
            {renderDropZone(
              "Attachments",
              "remarks_attachments",
              data.remarks_attachments,
              12
            )}
      
      
          {/* ===================== 2. ORDER SPECIFIC REQUIREMENTS ===================== */}
          {renderHeader("2. Order Specific Requirements")}
      
            <div className="col-12 mb-3">
              <Paper
                variant="outlined"
                sx={{ maxHeight: 220, overflowY: "auto", p: 1, bgcolor: "#fafafa" }}
              >
                {data.order_requirements?.length ? (
                  <Box sx={{ position: 'relative', pl: 1 }}>
                    {/* Vertical Timeline Line */}
                    <Box 
                        sx={{
                            position: 'absolute',
                            top: 10,
                            bottom: 10,
                            left: 5,
                            width: '2px',
                            backgroundColor: '#e0e0e0',
                            zIndex: 0
                        }} 
                    />

                    {data.order_requirements.map((log, index) => (
                      <Box key={index} sx={{ display: 'flex', mb: 2, position: 'relative', zIndex: 1 }}>
                        {/* Dot */}
                        <CircleIcon 
                            sx={{ 
                                fontSize: 12, 
                                color: index === 0 ? '#2d3d42' : '#bdbdbd',
                                backgroundColor: '#fafafa',
                                border: '3px solid #fafafa',
                                mr: 1.5,
                                mt: 0.5
                            }} 
                        />

                        <Box sx={{ flex: 1 }}>
                            <Typography variant="caption" display="block" sx={{ mb: 0.5, color: '#666', fontSize: '0.75rem', fontWeight: 500 }}>
                                {log.timestamp ? moment(log.timestamp).format('DD MMM YYYY, HH:mm') : '-'}
                            </Typography>

                            <Paper 
                                elevation={0} 
                                sx={{ 
                                    p: 1.5, 
                                    borderRadius: 2,
                                    bgcolor: '#f8f9fa', 
                                    border: '1px solid #f0f0f0', 
                                    '&:hover': {
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                        borderColor: '#d0d0d0'
                                    }
                                }}
                            >
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#333', fontSize: '11px', lineHeight: 1.3 }}>
                                    {log.comment || '-'}
                                </Typography>                                                        
                                
                                {log.attachments?.length > 0 && (
                                    <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
                                      {log.attachments.map((f, i) => (
                                        <Chip
                                          key={i}
                                          size="small"
                                          icon={<AttachFileIcon />}
                                          label={f.file_name || f.originalname || "attachment"}
                                          onClick={() => (f.file_url || f.s3_url) && window.open(f.file_url || f.s3_url, '_blank')}
                                          clickable={!!(f.file_url || f.s3_url)}
                                          variant="outlined"
                                          title={f.file_name || f.originalname || "attachment"}
                                          sx={{
                                              maxWidth: 200,
                                              bgcolor: 'white',
                                              '& .MuiChip-label': {
                                                  overflow: 'hidden',
                                                  textOverflow: 'ellipsis',
                                              },
                                          }}
                                        />
                                      ))}
                                    </Box>
                                )}

                                {log.added_by && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mt: 1 }}>
                                        By <strong>{log.added_by}</strong>
                                    </Typography>
                                )}
                            </Paper>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography fontSize={11} textAlign="center" color="text.secondary" sx={{ py: 2 }}>
                    No requirements added yet
                  </Typography>
                )}
              </Paper>
            </div>
        
            {!isReadOnly && (
              <div className="col-12 mb-3">
                <Box display="flex" flexDirection="column" gap={1}>
                  <Box display="flex" gap={1}>
                    <textarea
                      className="bom-input"
                      // rows={6}
                      placeholder="Add new requirement / reply..."
                      value={newLogComment}
                      onChange={(e) => setNewLogComment(e.target.value)}
                      style={{ flex: 1, height: "1500px !important" }} 
                    />
                    <Box display="flex" flexDirection="column" gap={1}>
                      <Button 
                        component="label" 
                        size="medium" 
                        variant="outlined"
                        sx={{ color: '#2d3d42', borderColor: '#2d3d42', '&:hover': { borderColor: '#1d2a2e', bgcolor: 'rgba(45, 61, 66, 0.04)' } }}
                      >
                        <AttachFileIcon fontSize="medium" />
                        <input
                          type="file"
                          hidden
                          multiple
                          onChange={(e) =>
                            setNewLogFiles([
                              ...newLogFiles,
                              ...Array.from(e.target.files).map(f => ({
                                file_name: f.name,
                                file_url: URL.createObjectURL(f),
                                file_type: f.type,
                                originFileObj: f,
                                uploaded_at: new Date()
                              }))
                            ])
                          }
                        />
                      </Button>
                      <Button
                        size="medium"
                        variant="contained"
                        onClick={handleAddLog}
                        disabled={!newLogComment && newLogFiles.length === 0}
                        sx={{
                          bgcolor: '#2d3d42',
                          color: '#ffffff !important',
                          '&:hover': {
                            bgcolor: '#1d2a2e',
                            color: '#ffffff !important',
                          },
                          '&:active': {
                            bgcolor: '#1d2a2e',
                            color: '#ffffff !important',
                          },
                          '&.Mui-disabled': {
                            backgroundColor: 'rgba(45, 61, 66, 0.6) !important',
                            color: '#ffffff !important',
                            WebkitTextFillColor: '#ffffff !important',
                            opacity: 0.7,
                          },
                        }}
                      >
                        <span style={{ color: '#ffffff' }}>Add</span>
                      </Button>
                    </Box>
                  </Box>
                  {newLogFiles.length > 0 && (
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {newLogFiles.map((f, i) => (
                        <Chip
                          key={i}
                          label={f.file_name}
                          size="small"
                          variant="outlined"
                          icon={<AttachFileIcon fontSize="small" />}
                          onDelete={() => setNewLogFiles(newLogFiles.filter((_, index) => index !== i))}
                          sx={{ borderColor: '#2d3d42', color: '#2d3d42', '& .MuiChip-deleteIcon': { color: '#2d3d42' } }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </div>
            )}
      
          {/* ===================== 3. FIXTURE VALIDATION COMPLETED ===================== */}
          {renderHeader("3. Fixture Validation Completed")}
            <div className="col-12 mb-3">
              <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: '4px', bgcolor: '#ffffff' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Checkbox
                      checked={data.validation_completed || false}
                      disabled={true}
                    />
                    <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#2d3d42' }}>
                        Validation Completed
                    </Typography>
                </Box>

                <div className="row mt-1">
                    {renderInput("Completion Date", "completion_date", "date", "", true, 4)}
                    {renderInput("KL Representative", "kl_representative", "text", "Name of representative", true, 4)}
                    {renderInput("Customer Representative", "customer_representative", "text", "Name of representative", true, 4)}
                    {renderDropZone("Sign-off Documents", "completion_attachments", data.completion_attachments, 12, "mb-3", isPanelReadOnly)}
                </div>
              </Paper>
            </div>
        
          {/* ===================== 4. PAYMENT FOLLOW-UP ===================== */}
          {renderHeader("4. Payment Follow-up")}
            <div className="col-12 mb-3">
              <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                {renderLabel("Payment Status", false, isReadOnly)}
                <select
                  className="form-control-customer bom-input"
                  value={commercialDetails?.payment_status || "Not Received"}
                  onChange={(e) => onCommercialUpdate?.({ payment_status: e.target.value })}
                  disabled={isReadOnly}
                >
                  <option value="Not Received">Not Received</option>
                  <option value="Received">Received</option>
                </select>
      
                {commercialDetails?.payment_status === "Received" && (
                  <Box mt={2}>
                    {renderLabel("Payment Details", true, isReadOnly)}
                    <textarea
                      className="form-control-customer bom-input"
                      rows={2}
                      value={data.payment_comments || ""}
                      onChange={(e) =>
                        handleChange("payment_comments", e.target.value)
                      }
                      disabled={isReadOnly}
                    />
                    {renderDropZone(
                      "Payment Proof",
                      "payment_attachments",
                      data.payment_attachments,
                      null,
                      "mt-3",
                      isReadOnly
                    )}
                  </Box>
                )}
              </Paper>
            </div>
        </div>
    );
      
};

export default ValidationPanel;